import { debounce } from 'lodash';
import axios from '../../Mixins/axios';

const axiosImageUrlCache = {};

export default {
    props: {
        src: {
            type: String,
            required: true,
        },
        bucket: {
            type: String,
            required: false,
            default: null
        },
        margin: {
            type: Number,
            required: false,
            default: 250,
        },
    },

    mixins: [axios],

    data() {
        return {
            observer: null,
            resizeDebouncer: null,

            // If the image requires a load from a backend source
            requiresLoad: true,

            // If the image was loaded at least once from a source
            wasLoaded: false,

            // If the image is currently loading
            isLoading: true,

            // If the image is currently visible in the DOM
            isVisible: false,

            // The resulting source used for the image
            imageSource: null,
        };
    },

    created() {
        this.resizeDebouncer = debounce(this.onResize, 250);

        window.addEventListener('resize', this.resizeDebouncer);
        window.addEventListener('orientationChange', this.resizeDebouncer);
    },

    mounted() {
        // If IntersectionObservers are possible, utilize them to delay loading
        // Otherwise, load the image
        this.supportsIntersection ?
            this.observe() :
            this.loadImage();
    },

    beforeDestroy() {
        // If IntersectionObservers are possible, destroy the current instance
        // Otherwise, null out
        if (this.supportsIntersection) {
            this.unobserve();
        }

        window.removeEventListener('resize', this.resizeDebouncer);
        window.removeEventListener('orientationChange', this.resizeDebouncer);
    },

    computed: {
        supportsIntersection() {
            return window.IntersectionObserver;
        },
        isDynamic() {
            return !!this.bucket;
        },
    },

    methods: {
        observe() {
            this.observer = new IntersectionObserver((entries, observer) => {
                entries.forEach((entry) => {
                    // Mark if the current element is visible or not
                    this.isVisible = entry.isIntersecting;

                    this.loadImage();
                });
            }, {
                root: null,
                rootMargin: `${this.margin}px`,
                threshold: 0
            });

            this.observer.observe(this.$el);
        },
        unobserve() {
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }
        },
        onResize() {
            // If the image doesn't already require a load and object fit is a variable one
            if (!this.requiresLoad && ['fill', 'contain', 'cover'].includes(this.objectFit())) {
                // Mark the image as requiring a load
                this.requiresLoad = true;
            }

            this.loadImage();
        },
        loadImage() {
            // Only load (or refresh) an image if it is visible and requires a new load
            if (this.isVisible && this.requiresLoad) {
                const resolver = this.isDynamic ?
                    this.buildFromAxios() :
                    this.buildFromUrl();

                resolver.then((url) => {
                    // If the resolved url is a new endpoint
                    if (url && this.imageSource !== url) {
                        const asyncLoader = new Image();
                        asyncLoader.onload = () => {
                            this.loadingComplete(url);
                        };
                        asyncLoader.src = url;
                    }
                });
            }
        },
        buildFromUrl() {
            return new Promise((resolve) => {
                // Loading from a url only needs to be done a single time
                if (!this.wasLoaded) {
                    this.isLoading = true;

                    resolve(this.src);
                }

                resolve(null);
            });
        },
        buildFromAxios() {
            this.isLoading = true;
            const endpoint = this.axiosEndpointUrl();

            // If the axios image cache has not built the image url yet, submit the request
            if (!(endpoint in axiosImageUrlCache)) {
                // // Use axios to dynamically fetch a new variation of the image from a storage bucket.
                axiosImageUrlCache[endpoint] = this.get(endpoint).then((response) => {
                    return response.data;
                });
            }

            return axiosImageUrlCache[endpoint];
        },
        axiosEndpointUrl() {
            const urlBuilder = new URL(`${window.location.origin}/image`);

            urlBuilder.searchParams.append('s', String(this.src));
            urlBuilder.searchParams.append('b', String(this.bucket));
            urlBuilder.searchParams.append('h', String(this.$el.clientHeight));
            urlBuilder.searchParams.append('w', String(this.$el.clientWidth));
            urlBuilder.searchParams.append('fit', String(this.objectFit()));

            return urlBuilder.href;
        },
        objectFit() {
            const bgSize = window.getComputedStyle(this.$el).backgroundSize;
            const objFit = window.getComputedStyle(this.$el).objectFit;

            return bgSize === 'auto' ? objFit : bgSize;
        },
        loadingComplete(url) {
            this.imageSource = url;

            this.wasLoaded = true;
            this.isLoading = false;
            this.requiresLoad = false;
        },
    },
};
